/**
 * The opportunity list's filter state — **all of it in the URL query string**
 * (Phase 4). A filtered view is then a link: pasteable into Slack, bookmarkable,
 * and survivable across a refresh, which is what makes the three built-in
 * saved views plain presets rather than a `saved_views` table.
 *
 * The one thing that is in the URL but **must not be in the request** is the
 * free-text term. §9: search is `POST` with the term in the body, because API
 * Gateway's access-log format excludes the query string precisely so search
 * terms never reach it. `toRequest()` is where that separation is enforced —
 * it returns the term separately from the filters, and the caller chooses
 * `POST /search` when it is present.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ALL_STATUSES_TOKEN, OPPORTUNITY_FLAG_FILTER_KEYS } from '@menlo/shared';

/** Every filter the list understands, matching `opportunityFilterSchema`. */
export const FILTER_KEYS = [
  'stage',
  'status',
  'ownerId',
  'faeId',
  'accountId',
  // Session 6: `exact` narrows `accountId` to the one account, without its sites.
  'accountScope',
  'repFirmId',
  'businessUnit',
  'segment',
  'region',
  'forecastCategory',
  'priceBasis',
  'stalled',
  'closingBefore',
  'rampBetween',
  'minValue',
  'maxValue',
  // Focus and Verified (session 4), from the shared list analytics uses too.
  ...OPPORTUNITY_FLAG_FILTER_KEYS,
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

/**
 * **The page's default, not the API's.** With no `status` in the URL the list
 * shows live deals and design wins (docs/bugs.md 2026-10-10, session 1 part
 * 3). A request with no `status` returns every status except Invalid
 * (decision 12, session 3), so the export and every other caller keep their
 * meaning. That is
 * why the default is applied here, where the URL is read, and is then sent as
 * an ordinary explicit `status=open,won`.
 */
export const DEFAULT_STATUSES = ['open', 'won'] as const;

/**
 * `status=all` in the URL: every status but Invalid, i.e. no status filter in
 * the request — the API leaves Invalid out unless it is named. Needed because
 * an ABSENT status now means the default, so "every status" needs a spelling
 * of its own to be bookmarkable. The list sends no status for it; the API
 * reads the same token the same way (`expandStatusFilter`), so a direct call
 * with `status=all` agrees with the page.
 */
export const ALL_STATUSES = ALL_STATUSES_TOKEN;

export interface ListState {
  filters: Partial<Record<FilterKey, string>>;
  /** Client state only. Never sent as a query parameter (§9). */
  term: string;
  sort: string | null;
  page: number;
  view: 'table' | 'board';
}

export function readListState(params: URLSearchParams): ListState {
  const filters: Partial<Record<FilterKey, string>> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  if (filters.status === undefined) filters.status = DEFAULT_STATUSES.join(',');
  else if (filters.status === ALL_STATUSES) delete filters.status;
  const page = Number(params.get('page') ?? '1');
  return {
    filters,
    term: params.get('q') ?? '',
    sort: params.get('sort'),
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    view: params.get('view') === 'board' ? 'board' : 'table',
  };
}

export interface ListRequest {
  /** Filters, as the API names them. */
  filters: Record<string, string>;
  /** Present only when the user typed something; sent in the BODY. */
  term: string | null;
  sort: string | null;
  page: number;
}

export function toRequest(state: ListState): ListRequest {
  return {
    filters: { ...state.filters },
    term: state.term.trim() === '' ? null : state.term.trim(),
    sort: state.sort,
    page: state.page,
  };
}

/** `?a=1&b=2`, filters only — for the list GET. */
export function toQueryString(request: ListRequest, pageSize: number): string {
  const params = new URLSearchParams(request.filters);
  params.set('page', String(request.page));
  params.set('pageSize', String(pageSize));
  if (request.sort !== null) params.set('sort', request.sort);
  return params.toString();
}

export function useListState(): {
  state: ListState;
  params: URLSearchParams;
  setFilter: (key: FilterKey, value: string | null) => void;
  setMany: (updates: Record<string, string | null>) => void;
  clearFilters: () => void;
  activeFilterCount: number;
} {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readListState(params), [params]);

  const setMany = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      // Any change to the filter set invalidates the page number — page 7 of a
      // narrower result is an empty screen that looks like "no results".
      if (!Object.prototype.hasOwnProperty.call(updates, 'page')) next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setFilter = useCallback(
    (key: FilterKey, value: string | null) => setMany({ [key]: value }),
    [setMany],
  );

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(params);
    for (const key of FILTER_KEYS) next.delete(key);
    next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
  }, [params, setParams]);

  return {
    state,
    params,
    setFilter,
    setMany,
    clearFilters,
    // Counted from the URL, not from `state`: the default status is not a
    // filter anybody set, and `Clear` has nothing to clear while it is the
    // only one. `status=all` does count — clearing it restores the default.
    activeFilterCount:
      FILTER_KEYS.filter((key) => (params.get(key) ?? '') !== '').length +
      (state.term === '' ? 0 : 1),
  };
}

/**
 * The three built-in views, shipped as URL presets pinned to the left nav.
 * There is no `saved_views` table; a user's own views live in `localStorage`.
 */
export const BUILT_IN_VIEWS = [
  {
    id: 'my-open',
    label: 'My open opportunities',
    /** `ownerId` is filled in with the signed-in user at render time. */
    query: 'status=open&mine=1',
  },
  {
    id: 'ramping',
    label: 'Ramping this year',
    query: 'status=open,won',
  },
  {
    id: 'needs-attention',
    label: 'Needs attention',
    /** Every status, as before the Open + Won default: won and on-hold deals stall too. */
    query: 'status=all&stalled=any',
  },
] as const;

export const SAVED_VIEWS_STORAGE_KEY = 'menlo-funnel.saved-views';

export interface SavedView {
  id: string;
  label: string;
  query: string;
}

export function loadSavedViews(): SavedView[] {
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch {
    return [];
  }
}

export function storeSavedViews(views: SavedView[]): void {
  window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views));
}
